import { z } from "zod";

const organizationSchema = z.object({
    "@id": z.string(),
    id: z.number(),
    name: z.string(),
});

/**
 * Pure-validation module for the "Create element" packaging form
 * (`POST /api/organizations/{organizationId}/packaging-elements`).
 *
 * Lives in its own file so unit tests can import the schema without
 * pulling in MUI / @foodpilot/foods, both of which currently break
 * Vitest module resolution (cf. ProductAccordion ESM bundling issue).
 *
 * The cross-field invariant (`recycled` ⇔ `recycledProportion`) mirrors
 * the Symfony side enforced by `PackagingElement::validateRecycled()`.
 */
export const elementSchema = z
    .object({
        name: z.string().trim().min(1, "form.required_field"),
        externalId: z.string().optional(),
        packagingReferenceId: z.number().int().positive("form.required_field"),
        weight: z.number().positive("form.required_field"),
        recycled: z.boolean(),
        recycledProportion: z.number().min(0).max(100).nullable(),
        recyclable: z.boolean().nullable(),
        reuse: z.boolean(),
        shares: z.array(organizationSchema),
    })
    .superRefine((data, ctx) => {
        if (!data.recycled && data.recycledProportion !== null) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                path: ["recycledProportion"],
                message: "packaging_element.recycled_proportion_must_be_null_when_not_recycled",
            });
        }
        if (data.recycled && data.recycledProportion === null) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                path: ["recycledProportion"],
                message: "packaging_element.recycled_proportion_required",
            });
        }
    });

export type ElementFormData = z.infer<typeof elementSchema>;
